export type CapAlertTemplateKey =
  | 'warning'
  | 'srpProtocol'
  | 'flashing'
  | 'emergency'
  | 'caution';

/**
 * How the catalog names the five CAP alert templates, in one place so the
 * matching is adjusted in one edit. A template is recognised by its catalog
 * name, never by a product code, so the page does not depend on the codes
 * differing between environments.
 */
export const CAP_ALERT_TEMPLATE_NAMES: Record<CapAlertTemplateKey, string> = {
  warning: 'CAP Alert Warning',
  srpProtocol: 'CAP Alert SRP Protocol',
  flashing: 'CAP Alert Flashing',
  emergency: 'CAP Alert Emergency',
  caution: 'CAP Alert Caution',
};

/** Matching ignores case, surrounding and repeated spaces, and the leading "CAP Alert". */
function normalizeTemplateName(name: string | undefined): string {
  return (name ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/^cap alert\b\s*/, '');
}

const KEYS_BY_NAME = new Map<string, CapAlertTemplateKey>(
  (
    Object.entries(CAP_ALERT_TEMPLATE_NAMES) as [CapAlertTemplateKey, string][]
  ).map(([key, name]) => [normalizeTemplateName(name), key]),
);

/** Which of the five known templates a catalog template is, if any. */
export function capAlertTemplateKey(
  template: { name?: string } | null | undefined,
): CapAlertTemplateKey | undefined {
  return KEYS_BY_NAME.get(normalizeTemplateName(template?.name));
}

/** The catalog template that is the given known template, if the catalog has it. */
export function findCapAlertTemplate<T extends { name?: string }>(
  templates: readonly T[],
  key: CapAlertTemplateKey,
): T | undefined {
  return templates.find((template) => capAlertTemplateKey(template) === key);
}

const STATIC_TEMPLATE_DESCRIPTION =
  'Static template that only passes the provider’s text message and does not change icon or style based on alert type.';

const ICON_TEMPLATE_DESCRIPTION =
  'Unique icons illustrate the type of emergency at hand (fire, tornado, flood, etc).';

const SRP_TEMPLATE_DESCRIPTION =
  'Offers unique icons and colors for each alert type in accordance with the Standard Response Protocol standards and best practices.';

/** What the page knows about the catalog's templates; the catalog itself carries none of it. */
export const CAP_ALERT_TEMPLATES = {
  /** Card description by known template. */
  descriptions: {
    warning: STATIC_TEMPLATE_DESCRIPTION,
    emergency: STATIC_TEMPLATE_DESCRIPTION,
    flashing: ICON_TEMPLATE_DESCRIPTION,
    caution: ICON_TEMPLATE_DESCRIPTION,
    srpProtocol: SRP_TEMPLATE_DESCRIPTION,
  } as Record<CapAlertTemplateKey, string>,

  /** Templates whose own design shows the brand logo, so it defaults on. */
  brandLogoDefaultOn: [
    'srpProtocol',
    'flashing',
  ] as readonly CapAlertTemplateKey[],

  /** The alert conditions SRP Protocol can preview, in display order. */
  srpConditions: [
    'Default',
    'Hold',
    'Secure',
    'Lockdown',
    'Evacuate',
    'Shelter',
  ] as const,

  customPresentation: {
    title: 'Custom Presentation',
    description:
      'Show one of your own presentations as the alert. Layout and styling are set in the editor.',
  },
};

/** A template the constant does not know has no description. */
export function capAlertTemplateDescription(
  template: { name?: string } | null | undefined,
): string {
  const key = capAlertTemplateKey(template);
  return key ? CAP_ALERT_TEMPLATES.descriptions[key] : '';
}
